import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const S3_URL  = process.env.REACT_APP_S3_BASE_URL;

import TopBar from '../components/TopBar';
import HeroSlider from '../components/HeroSlider';
import BenefitsRow from '../components/BenefitsRow';
import MultiRowIcon from '../components/MultiRowIcon';
import DualSectionSlider from '../components/DualSectionSlider';
import ProductCarousel from '../components/ProductCarousel';
import SimpleBanner from '../components/SimpleBanner';
import MasonryGrid from '../components/MasonryGrid';

import sendEmailIcon from '../assets/icons/send-email.svg';
import selectProductIcon from '../assets/icons/select-product.svg';
import formIcon from '../assets/icons/form.svg';

import slide1 from '../assets/slider/sublimacion.png';
import slide2 from '../assets/slider/serigrafia.png';
import slide3 from '../assets/slider/screen.png';
import slide4 from '../assets/slider/laser.png';
import slide5 from '../assets/slider/bordado.png';

import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import {
  generateWebsiteSchema,
  generateOrganizationSchema,
} from '../utils/schemaGenerators';
import '../styles/pages/Home.scss';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.promocionalesenstock.com';

const multiRowData = [
  {
    title: 'Selecciona tu producto y cantidades',
    subtitle:
      'Escoge el producto que deseas personalizar y ajusta la cantidad según tus necesidades. Puedes explorar nuestro catálogo para encontrar los artículos que mejor se adapten a tu proyecto.',
    icon: selectProductIcon,
  },
  {
    title: 'Personaliza tu solicitud de compra',
    subtitle:
      'Incorpora los detalles de personalización y las especificaciones de tu pedido. Completa el formulario con las características especiales que deseas para tu producto y proporciona tu información personal.',
    icon: formIcon,
  },
  {
    title: 'Envía tu solicitud de cotización',
    subtitle:
      'Una vez que hayas completado los detalles de tu pedido, envía tu solicitud de cotización. Nuestro equipo revisará tu solicitud y se pondrá en contacto contigo con la información y precios detallados.',
    icon: sendEmailIcon,
  },
];

const dualSlidesData = [
  {
    miniTitle: 'Nuestras técnicas de marcación',
    heading: 'Sublimación',
    description:
      'La sublimación es una técnica de impresión que utiliza calor para transferir una imagen a un material, generalmente poliéster o productos con un recubrimiento especial. Es ideal para tazas, textiles y artículos que necesitan imágenes a todo color de alta calidad y durabilidad.',
    img: slide1,
  },
  {
    miniTitle: 'Nuestras técnicas de marcación',
    heading: 'Serigrafía',
    description:
      'La serigrafía es un método de impresión que utiliza una malla tensada sobre un marco para transferir tinta a un objeto. Es ideal para artículos promocionales como camisetas, esferos y bolsas, y permite imprimir diseños de uno o varios colores de manera precisa y económica.',
    img: slide2,
  },
  {
    miniTitle: 'Nuestras técnicas de marcación',
    heading: 'Screen Printing',
    description:
      'La impresión en pantalla (o serigrafía) es una variante que implica el uso de una plantilla o malla para aplicar tinta sobre un objeto. Es muy versátil y se puede usar en una variedad de superficies, incluyendo plástico, metal y textiles. Es una técnica muy popular para la personalización de esferos, camisetas y objetos planos.',
    img: slide3,
  },
  {
    miniTitle: 'Nuestras técnicas de marcación',
    heading: 'Láser',
    description:
      'El grabado láser utiliza un rayo de luz altamente concentrado para marcar la superficie de un objeto con precisión. Es perfecto para artículos de metal, madera, cristal y algunos plásticos. Esta técnica ofrece un acabado limpio, permanente y resistente al desgaste.',
    img: slide4,
  },
  {
    miniTitle: 'Nuestras técnicas de marcación',
    heading: 'Bordado',
    description:
      'El bordado es un proceso de costura de un diseño en telas o prendas utilizando hilos de diferentes colores. Es ideal para textiles como gorras, camisetas y bolsas, y proporciona un acabado elegante y duradero. Es muy apreciado por su resistencia al lavado y su aspecto de alta calidad.',
    img: slide5,
  },
];

const Home = () => {
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/products/deals`, {
      headers: { authorization: token },
    })
      .then(res => setDeals(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const thumbUrl = (p) =>
    p?.image_files?.length ? `${S3_URL}/thumbnails/${p.image_files[0]}` : null;

  const pageTitle = 'Promocionales en Stock – Catálogo de productos promocionales personalizados en Colombia.';
  const pageDescription =
    'Catálogo de productos promocionales personalizados en Colombia. Merchandising corporativo, regalos empresariales y artículos publicitarios.';
  const canonicalUrl = '/';
  const additionalMetaTags = [
    { name: 'robots', content: 'index,follow,max-image-preview:large' },
    {
      name: 'keywords',
      content:
        'promocionales personalizados, regalos corporativos, artículos publicitarios, merchandising en Colombia',
    },
    { name: 'author', content: 'Promocionales en Stock' },
  ];

  const jsonLdSchema = {
    '@context': 'https://schema.org',
    '@graph': [generateWebsiteSchema(), generateOrganizationSchema()],
  };

  return (
    <>
      {/* == SEO Component == */}
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        prevPageUrl={null}
        nextPageUrl={null}
        ogImage="/images/og-default.jpg"
        ogType="website"
        jsonLd={jsonLdSchema}
        additionalMeta={additionalMetaTags}
        siteUrl={SITE_URL}
      />

      {/* == Contenido principal de Home == */}
      <TopBar />

      <HeroSlider />

      <BenefitsRow />

      <ProductCarousel
        id="first-random"
        sectionTitle="Los más vendidos!"
        sectionSubtitle="Elija de nuestra enorme colección personalizable de bolígrafos, vasos y botellas, tecnología y más."
        endpoint="random-products"
      />

      <section className="Home__deals-section" id="deals">
        <div className="Home__deals-banner">
          <div className="Home__deals-banner-inner container">
            <div className="Home__deals-banner-left">
              <span className="Home__deals-flame" aria-hidden="true">🔥</span>
              <div>
                <p className="Home__deals-eyebrow">Promociones exclusivas</p>
                <h2 className="Home__deals-banner-title">Ofertas especiales</h2>
                <p className="Home__deals-banner-sub">Favoritos imperdibles · Existencias limitadas</p>
              </div>
            </div>
            <Link to="/productos/todos" className="Home__deals-banner-cta">
              Ver todos los productos →
            </Link>
          </div>
        </div>
        <div className={`Home__deals-track container${deals.length > 5 ? ' Home__deals-track--scroll' : ''}`}>
          {deals.map((p) => {
            const thumb = thumbUrl(p);
            return (
              <Link key={p.id || p.slug} to={`/productos/${p.slug}`} className="Home__deal-card">
                <div className="Home__deal-img-wrap">
                  <span className="Home__deal-badge">OFERTA</span>
                  {thumb
                    ? <img src={thumb} alt={p.title} loading="lazy"
                        onError={e => { e.currentTarget.style.display = 'none'; }} />
                    : <div className="Home__deal-placeholder">📷</div>
                  }
                </div>
                <div className="Home__deal-info">
                  {p.inventoryTotal > 0 && (
                    <span className="Home__deal-stock">En Stock: {p.inventoryTotal.toLocaleString('es-CO')}</span>
                  )}
                  <p className="Home__deal-title">{p.title}</p>
                </div>
                <span className="Home__deal-cta">Cotizar</span>
              </Link>
            );
          })}
        </div>
      </section>

      <SimpleBanner totalProducts="4.160" totalColors="10.116" />

      <ProductCarousel
        id="second-random"
        sectionTitle="¡Novedades Imperdibles!"
        sectionSubtitle="Descubre nuestra más reciente colección de bolígrafos, vasos, botellas, tecnología y mucho más, listos para personalizar."
        endpoint="random-products"
      />

      <MasonryGrid />

      <DualSectionSlider title="Nuestras técnicas de marcación" slides={dualSlidesData} />

      <MultiRowIcon
        sectionTitle="¡Encuentra el producto perfecto para ti!"
        sectionSubtitle="Elige entre nuestra enorme colección personalizable de bolígrafos, vasos, botellas, tecnología y mucho más."
        content={multiRowData}
      />

      <BenefitsRow />
    </>
  );
};

export default Home;
